import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faBolt, faCarBattery, faXmark } from '@fortawesome/free-solid-svg-icons';
import DeviceSearchSelect from '../components/compatibility/DeviceSearchSelect';
import WyborModeluZgodnosci from '../components/compatibility/WyborModeluZgodnosci';
import ListaParZgodnosci from '../components/compatibility/ListaParZgodnosci';
import PanelKompatybilnych from '../components/compatibility/PanelKompatybilnych';
import CompatibilityResultCard from '../components/compatibility/CompatibilityResultCard';
import CompatibilityAnimation from '../components/compatibility/CompatibilityAnimation';
import {
  checkCompatibility, getInverterById, getBatteryById, checkModelsCompatibility,
} from '../api/compatibility';
import useDocumentTitle from '../hooks/useDocumentTitle';
import BackButton from '../components/BackButton';
import { useTranslation } from 'react-i18next';
import { kluczKraju } from '../kraj';
import { krotkaNazwa } from '../lib/projekt3d/katalog';

// Wynik pełnego sprawdzenia pary modeli (oba wybrane) — cienki wrapper wołający
// /check-models i renderujący tę samą kartę wyniku co stara ścieżka ofertowa
// (identyczny kształt odpowiedzi, patrz api/compatibility.js).
const ModelPairResult = ({ inverterModelId, batteryModelId, onWynik }) => {
  const { t } = useTranslation('zgodnosc');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    onWynik?.(null); // podczas sprawdzania animacja wraca do stanu neutralnego
    checkModelsCompatibility(inverterModelId, batteryModelId)
      .then((res) => { if (!cancelled) { setResult(res); setLoading(false); onWynik?.(res); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); onWynik?.(null); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inverterModelId, batteryModelId]);

  if (loading && !result) {
    return <div className="h-48 rounded-2xl bg-gray-100 animate-pulse" aria-busy="true" aria-label={t('sprawdzanie')} />;
  }
  if (error) {
    return <p className="text-sm text-rose-500">{t('blad_pary')}</p>;
  }
  return result ? <CompatibilityResultCard result={result} /> : null;
};

// Zapamiętanie wyboru między wejściami na stronę modelu (sessionStorage — żyje w
// obrębie karty, znika po jej zamknięciu). Dzięki temu po kliknięciu „Sprawdź
// dostępność" i powrocie kombinacja jest odtworzona.
const PAGE_KEY = kluczKraju('compat:page');
const loadPage = () => {
  try { return JSON.parse(sessionStorage.getItem(PAGE_KEY)) || {}; } catch { return {}; }
};

// Pozycja z listy kompatybilnych -> kształt „picka" selektora (kafelek + /check-models).
// Lista modelowa nie zwraca img_url/pojemności, więc kafelek pokaże placeholder i
// samego producenta — kluczowe pola (id, cena, dostępność, link modelu) są obecne.
const itemToPick = (item) => ({
  id: item.id,
  name: item.product_name,
  manufacturer: item.manufacturer,
  img_url: item.img_url ?? null,
  price_min: item.price_min,
  availability_any: item.availability_any,
  model_url: item.model_url,
});

// user_status z /check-models -> stan animacji. compatible -> pełny przepływ,
// incompatible -> rozłączenie, reszta (conditional/conflicting/unknown) -> słaby
// przepływ „wymaga weryfikacji". null (ładowanie/błąd) -> stan neutralny (waiting).
const ANIM_BY_STATUS = { compatible: 'compatible', incompatible: 'not_compatible' };
const toAnimStatus = (userStatus) => (userStatus ? (ANIM_BY_STATUS[userStatus] || 'unknown') : 'waiting');

// Krótki opis połączenia pod kablem w hero: protokół i wymagane ustawienie falownika, gdy wynik je
// podaje („CAN, kod 00”). Długie ustawienia (całe zdania z instrukcji) zostają w karcie wyniku.
const USTAWIENIE_MAX = 24;
export const szczegolPary = (wynik) => {
  if (!wynik) return null;
  const ustawienie = (wynik.conditions || []).find((c) => c.type === 'setting')?.value_pl;
  const czesci = [wynik.communication_protocol, ustawienie && ustawienie.length <= USTAWIENIE_MAX ? ustawienie : null]
    .map((x) => (x ? String(x).replace(/\s*\u2014\s*/g, ', ').trim() : null))
    .filter(Boolean);
  return czesci.length ? czesci.join(', ') : null;
};

// Strona /kompatybilnosc.
// Główny tryb: wybór WŁASNYCH modeli katalogu (pole z lupą albo listy Producent,
// Seria, Moc, jak w projekcie 3D) -> propozycje kompatybilnych modeli.
// Pod narzędziem spis stron par (/kompatybilnosc/<slug>).
// Tryb zaawansowany (dawna „baza wiedzy"): wolne wyszukiwanie po nazwie
// dowolnej oferty, zostaje bez zmian, tylko schowany pod przełącznikiem.
const Compatibility = () => {
  const { t } = useTranslation('zgodnosc');
  useDocumentTitle(t('tytul'));

  // `zrodlo` = urządzenie, OD KTÓREGO szukamy. Zastąpiło „swapped": tamto mówiło
  // tylko o kolejności kolumn, a nie o tym, co jest punktem wyjścia, a co wynikiem.
  const [zrodlo, setZrodlo] = useState(() => loadPage().zrodlo || 'inverter');
  const [pickedInverter, setPickedInverter] = useState(() => loadPage().pickedInverter || null);
  const [pickedBattery, setPickedBattery] = useState(() => loadPage().pickedBattery || null);
  const [pairResult, setPairResult] = useState(null); // ostatnia odpowiedź /check-models
  const [paraOtwarta, setParaOtwarta] = useState(false);
  // Ile pasujących urządzeń znalazł panel po prawej (null = jeszcze szuka).
  const [liczbaWynikow, setLiczbaWynikow] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Zapis stanu przy każdej zmianie — do odtworzenia po powrocie na stronę.
  useEffect(() => {
    try {
      sessionStorage.setItem(PAGE_KEY, JSON.stringify({ zrodlo, pickedInverter, pickedBattery }));
    } catch { /* brak dostępu do storage */ }
  }, [zrodlo, pickedInverter, pickedBattery]);

  /**
   * Zmiana kierunku NIC NIE KASUJE — oba wybory zostają.
   *
   * Wcześniej przełączenie czyściło drugą stronę, więc „mam falownik" -> „mam
   * magazyn" -> z powrotem gubiło falownik, którego user mozolnie wyszukał.
   * Żeby jednocześnie nie wyskakiwał werdykt pary, której nikt nie prosił,
   * widok pary trzyma OSOBNA flaga `paraOtwarta`: podnosi ją dopiero kliknięcie
   * w kandydata, a przełączenie kierunku ją opuszcza.
   */
  const zmienZrodlo = (nowe) => {
    if (nowe === zrodlo) return;
    setParaOtwarta(false);
    setZrodlo(nowe);
  };

  const InverterPicker = <WyborModeluZgodnosci kind="inverter" label={t('falownik_hybrydowy')} value={pickedInverter} onChange={setPickedInverter} />;
  const BatteryPicker = <WyborModeluZgodnosci kind="battery" label={t('magazyn_energii')} value={pickedBattery} onChange={setPickedBattery} />;

  const zrodloModel = zrodlo === 'inverter' ? pickedInverter : pickedBattery;
  const paraWybrana = paraOtwarta && !!(pickedInverter && pickedBattery);

  /**
   * Animacja hero:
   *  · otwarta para  -> werdykt tej pary,
   *  · są wyniki     -> pełny przepływ (znaleźliśmy pasujące urządzenia),
   *  · brak wyników  -> „wymaga weryfikacji", bo modelu nie ma w tabeli zgodności,
   *  · nic nie wybrane albo trwa szukanie -> stan neutralny.
   */
  const animStatus = paraWybrana
    ? toAnimStatus(pairResult?.user_status)
    : zrodloModel && liczbaWynikow != null
      ? (liczbaWynikow > 0 ? 'compatible' : 'unknown')
      : 'waiting';

  // Podpisy w hero: urządzenie, od którego zaczynamy, a drugie dopiero w otwartej parze. Drugi wybór
  // zostaje w pamięci po zamknięciu pary, ale wtedy hero nie pokazuje werdyktu tej pary, więc i jej nazwy.
  // Nazwa bez słów sklepu i producenta („Inwerter - DEYE - SUN-10K…” -> „SUN-10K…”), bo podpis jest wąski.
  const nazwaHero = (m) => (m ? (krotkaNazwa(m.name, m.manufacturer) || m.name || null) : null);
  const heroFalownik = (zrodlo === 'inverter' || paraWybrana) ? nazwaHero(pickedInverter) : null;
  const heroMagazyn = (zrodlo === 'battery' || paraWybrana) ? nazwaHero(pickedBattery) : null;
  const heroSzczegol = paraWybrana ? szczegolPary(pairResult) : null;

  // Wybrany kandydat — do podświetlenia kafelka na liście, żeby po otwarciu
  // wyniku było widać, KTÓREGO z pasujących urządzeń dotyczy panel obok.
  const wybranyKandydatId = paraWybrana
    ? (zrodlo === 'inverter' ? pickedBattery?.id : pickedInverter?.id)
    : null;

  return (
    /* Kontener rozszerza się dopiero z trzecią kolumną. Stały `max-w-7xl`
       rozciągałby dwukolumnowy widok startowy na całą szerokość ekranu,
       a wtedy wybór modelu i lista pływałyby daleko od siebie. */
    <div className={`${paraWybrana ? 'max-w-7xl' : 'max-w-5xl'} mx-auto px-4 md:px-8 mt-8 mb-16 transition-[max-width]`}>
      <div className="mb-1"><BackButton /></div>
      <div className="mb-2"><CompatibilityAnimation status={animStatus} falownik={heroFalownik} magazyn={heroMagazyn} szczegol={heroSzczegol} /></div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center">{t('tytul')}</h1>
      <p className="text-sm text-gray-500 mb-6 max-w-2xl mx-auto text-center">
        {t('wstep')}
      </p>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-7">
        {/* Kierunek szukania jako DWA PRZYCISKI, nie ikona zamiany. Strzałki „⇄" nie
            mówiły, co się zamienia ani co jest teraz punktem wyjścia; tu widać wprost,
            od czego zaczynamy i że wynik po prawej jest drugą stroną pary. */}
        <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1 mb-5">
          {[
            { key: 'inverter', label: t('mam_falownik'), icon: faBolt },
            { key: 'battery', label: t('mam_magazyn'), icon: faCarBattery },
          ].map(({ key, label, icon }) => {
            const aktywny = zrodlo === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => zmienZrodlo(key)}
                aria-pressed={aktywny}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  aktywny ? 'bg-white text-blue-700 shadow-sm border border-blue-200' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <FontAwesomeIcon icon={icon} className="text-xs" />
                {label}
              </button>
            );
          })}
        </div>

        {/* Trzecia kolumna DOCHODZI, a nie podmienia listy: werdykt pary ma stać
            OBOK pasujących urządzeń, żeby dało się kliknąć kolejne i porównać.
            Wcześniej wynik wchodził w miejsce listy, więc powrót do niej wymagał
            osobnego kliknięcia, a kontekst „z czego wybieram" znikał z ekranu. */}
        <div className={`grid gap-6 items-start ${paraWybrana ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
          {/* Lewa: wybór urządzenia, od którego zaczynamy. */}
          <div className="min-w-0">
            {zrodlo === 'inverter' ? InverterPicker : BatteryPicker}
          </div>

          {/* Środek: pasujące urządzenia. Pojawiają się OD RAZU po wskazaniu modelu. */}
          <div className="min-w-0 lg:border-l lg:border-gray-100 lg:pl-6">
            {zrodloModel ? (
              <PanelKompatybilnych
                kierunek={zrodlo}
                modelId={zrodloModel.id}
                onLiczba={setLiczbaWynikow}
                wybranyId={wybranyKandydatId}
                onWybierz={(item) => {
                  if (zrodlo === 'inverter') setPickedBattery(itemToPick(item));
                  else setPickedInverter(itemToPick(item));
                  setParaOtwarta(true);
                }}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 px-5 py-10 text-center">
                <p className="text-sm text-gray-600 font-medium">
                  {t('wybierz_model')}
                </p>
                <p className="text-xs text-gray-400 mt-1 leading-5">
                  {zrodlo === 'inverter' ? t('puste_magazyny') : t('puste_falowniki')}
                </p>
              </div>
            )}
          </div>

          {/* Prawa: werdykt wybranej pary. Istnieje tylko wtedy, gdy jest co pokazać. */}
          {paraWybrana && (
            <div className="min-w-0 lg:border-l lg:border-gray-100 lg:pl-6">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h2 className="text-sm font-bold text-gray-900">{t('wynik_pary')}</h2>
                <button
                  type="button"
                  onClick={() => setParaOtwarta(false)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                >
                  <FontAwesomeIcon icon={faXmark} className="text-[11px]" />
                  {t('zamknij')}
                </button>
              </div>
              <ModelPairResult
                inverterModelId={pickedInverter.id}
                batteryModelId={pickedBattery.id}
                onWynik={setPairResult}
              />
            </div>
          )}
        </div>
      </div>

      {/* Droga do stron par (/kompatybilnosc/<slug>): wcześniej znał je tylko Google. */}
      <ListaParZgodnosci />

      <div className="mt-6 pt-6 border-t border-gray-100">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900"
        >
          <FontAwesomeIcon icon={faChevronDown} className={`text-xs transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
          {t('baza_wiedzy')}
        </button>
        {advancedOpen && <AdvancedOfferCheck />}
      </div>
    </div>
  );
};

// Dawna ścieżka: wolne wyszukiwanie po nazwie dowolnej oferty (nie tylko naszych
// modeli) + pojedyncze sprawdzenie pary. Bez zmian względem wcześniejszej wersji
// strony — teraz tylko jako tryb dodatkowy, nie domyślny.
const AdvancedOfferCheck = () => {
  const { t } = useTranslation('zgodnosc');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialInverterId = searchParams.get('inverter_id');
  const initialBatteryId = searchParams.get('battery_id');

  const [inverter, setInverter] = useState(initialInverterId ? { id: initialInverterId, product_name: '', manufacturer: '' } : null);
  const [battery, setBattery] = useState(initialBatteryId ? { id: initialBatteryId, product_name: '', manufacturer: '' } : null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (initialInverterId && !initialBatteryId) {
      getInverterById(initialInverterId).then((found) => { if (found) setInverter(found); });
    }
    if (initialBatteryId && !initialInverterId) {
      getBatteryById(initialBatteryId).then((found) => { if (found) setBattery(found); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!inverter?.id || !battery?.id) { setResult(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(false);
    checkCompatibility(inverter.id, battery.id)
      .then((res) => {
        if (cancelled) return;
        setResult(res);
        setInverter((prev) => (prev && prev.product_name ? prev : { id: res.inverter.id, product_name: res.inverter.product_name, manufacturer: res.inverter.manufacturer }));
        setBattery((prev) => (prev && prev.product_name ? prev : { id: res.battery.id, product_name: res.battery.product_name, manufacturer: res.battery.manufacturer }));
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inverter?.id, battery?.id]);

  const onSelectInverter = (device) => {
    setInverter(device);
    const next = new URLSearchParams(searchParams);
    if (device) next.set('inverter_id', device.id); else next.delete('inverter_id');
    setSearchParams(next, { replace: true });
  };
  const onSelectBattery = (device) => {
    setBattery(device);
    const next = new URLSearchParams(searchParams);
    if (device) next.set('battery_id', device.id); else next.delete('battery_id');
    setSearchParams(next, { replace: true });
  };

  const retry = () => {
    if (inverter?.id && battery?.id) {
      setLoading(true);
      setError(false);
      checkCompatibility(inverter.id, battery.id)
        .then((res) => setResult(res))
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="mt-4">
      <p className="text-sm text-gray-500 mb-4">
        {t('zaawansowane_wstep')}
      </p>
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-7 grid sm:grid-cols-2 gap-4 sm:gap-6 relative z-10">
        <DeviceSearchSelect kind="inverter" label={t('falownik_hybrydowy')} placeholder={t('przyklad_falownik')} value={inverter} onChange={onSelectInverter} />
        <DeviceSearchSelect kind="battery" label={t('magazyn_energii')} placeholder={t('przyklad_magazyn')} value={battery} onChange={onSelectBattery} />
      </div>

      <div className="mt-6">
        {loading && !result ? (
          <div className="h-48 rounded-2xl bg-gray-100 animate-pulse" aria-busy="true" aria-label={t('sprawdzanie')} />
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700 flex items-center justify-between gap-3 flex-wrap">
            <span>{t('blad_polaczenia')}</span>
            <button type="button" onClick={retry} className="font-semibold underline shrink-0">{t('sprobuj_ponownie')}</button>
          </div>
        ) : result ? (
          <div className={loading ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'} aria-busy={loading}>
            <CompatibilityResultCard result={result} />
          </div>
        ) : inverter?.id || battery?.id ? (
          <p className="text-sm text-gray-400 text-center py-6">{t('wybierz_drugie')}</p>
        ) : null}
      </div>
    </div>
  );
};

export default Compatibility;
